import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });

    // Keep Shopify recommendation tracking for real user navigation, but do not expose
    // pr_* tracking variants as crawlable links in the rendered recommendation block.
    this.#sanitizeRecommendationLinks();
    this.addEventListener('click', this.#handleTrackedNavigation, true);
    this.addEventListener('auxclick', this.#handleTrackedNavigation, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.removeEventListener('click', this.#handleTrackedNavigation, true);
    this.removeEventListener('auxclick', this.#handleTrackedNavigation, true);
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      this.#sanitizeRecommendationLinks();
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
          this.#sanitizeRecommendationLinks();
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Replace only Shopify recommendation tracking parameters in crawlable hrefs.
   * The full tracked URL is retained in data-mml-tracked-url for real user navigation.
   * Non-tracking parameters such as ?variant= are preserved.
   */
  #sanitizeRecommendationLinks() {
    for (const link of this.querySelectorAll('a[href]')) {
      if (!(link instanceof HTMLAnchorElement)) continue;

      const rawHref = link.getAttribute('href');
      if (!rawHref) continue;

      let url;
      try {
        url = new URL(rawHref, window.location.origin);
      } catch {
        continue;
      }

      const trackingKeys = [...new Set([...url.searchParams.keys()].filter((key) => key.startsWith('pr_')))];
      if (trackingKeys.length === 0) continue;

      // Preserve the first full Shopify recommendation URL. Variant/product-card state can
      // subsequently update the clean href without losing the original recommendation context.
      if (!link.dataset.mmlTrackedUrl) {
        link.dataset.mmlTrackedUrl = rawHref;
      }

      for (const key of trackingKeys) {
        url.searchParams.delete(key);
      }

      link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
    }
  }

  /**
   * Before a genuine user navigation, temporarily restore the Shopify-tracked URL on all
   * links in the clicked product card. Capture phase ensures ProductCard sees the tracked
   * overlay URL before its own click handler runs. The clean href is restored afterwards.
   *
   * This covers normal click, keyboard-generated click, Ctrl/Cmd/Shift-click and middle-click.
   * @param {Event} event
   */
  #handleTrackedNavigation = (event) => {
    if (!(event.target instanceof Element)) return;

    const productCard = event.target.closest('product-card');
    if (!(productCard instanceof HTMLElement) || !this.contains(productCard)) return;

    const trackedLinks = productCard.querySelectorAll('a[data-mml-tracked-url]');
    for (const link of trackedLinks) {
      if (!(link instanceof HTMLAnchorElement)) continue;
      this.#armTrackedLink(link);
    }
  };

  /**
   * Restore Shopify pr_* parameters for the current user action while keeping the card's
   * current clean pathname, variant selection and other non-tracking query parameters.
   * @param {HTMLAnchorElement} link
   */
  #armTrackedLink(link) {
    const trackedHref = link.dataset.mmlTrackedUrl;
    const cleanHref = link.getAttribute('href');
    if (!trackedHref || !cleanHref) return;

    let trackedUrl;
    let cleanUrl;
    try {
      trackedUrl = new URL(trackedHref, window.location.origin);
      cleanUrl = new URL(cleanHref, window.location.origin);
    } catch {
      return;
    }

    // Product cards can change pathname (combined listings) and variant after rendering.
    // Adopt the current clean card state while retaining only Shopify's pr_* tracking data
    // from the original recommendation URL.
    trackedUrl.pathname = cleanUrl.pathname;
    trackedUrl.hash = cleanUrl.hash;

    const nonTrackingKeys = [
      ...new Set([...trackedUrl.searchParams.keys()].filter((key) => !key.startsWith('pr_'))),
    ];
    for (const key of nonTrackingKeys) {
      trackedUrl.searchParams.delete(key);
    }

    for (const [key, value] of cleanUrl.searchParams.entries()) {
      if (!key.startsWith('pr_')) trackedUrl.searchParams.append(key, value);
    }

    link.setAttribute('href', `${trackedUrl.pathname}${trackedUrl.search}${trackedUrl.hash}`);

    // Browser default navigation and ProductCard's click navigation run before the next timer task.
    window.setTimeout(() => {
      if (link.isConnected) link.setAttribute('href', cleanHref);
    }, 0);
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
